import { useForm, SubmitHandler } from "react-hook-form";
import { useState } from "react";
import CypherLeakLogo from "../../components/common/logo/CypherLeakLogo";
import InputForm from "../../components/common/input/InputText";
import { isDefined } from "../../utils/validation";
import PrimaryButton from "../../components/common/button/PrimaryButton";
import { ToastContainer } from "react-toastify";
import VerificationCodeInput from "../../components/common/input/VerificationCodeInput";
import SecondaryButton from "../../components/common/button/SeconaryButton";
import { Link} from "react-router-dom";
import { validEmailPattern, validPasswordPattern } from "../../utils/regex";
import { forgotPassword, recoverPassword } from '../../services/mutations/forgotPassword';
import useForgotPasswordMutations from "../../hooks/services/authentication/useForgotPasswordMutations";

type Inputs = {
  email: string;
  newPassword: string;
  confirmPassword: string;
};

export default function ForgotPassword() {
  // React Form Hook
  const { register, handleSubmit, formState: { errors } } = useForm<Inputs>();
  // Forgot Password States
  const [isValidEmail, setIsValidEmail] = useState<boolean>(false);
  const [isValidCode, setIsValidCode] = useState<boolean>(false);
  const [emailCode, setEmailCode] = useState<string | undefined>();
  // Reset Forgot Password Form/States
  const resetForm = () => {
    setIsValidEmail(false);
    setIsValidCode(false);
    setEmailCode(undefined);
  };
  // Forgot Password Service
  const {
    forgotPasswordMutation,
    codeRecoveryMutation,
    recoverPasswordMutation,
    isAnyMutationPending
  } = useForgotPasswordMutations();


  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    // Block statements if any request's pending
    if (isAnyMutationPending()) return;
    // When Email Address Input
    if (!isValidEmail) {
      forgotPasswordMutation.mutateAsync(forgotPassword(data.email))
        .then(response => response?.data?.forgotPassword && setIsValidEmail(true));
    }
    // When Verfification Code Input
    if (isValidEmail && emailCode && !isValidCode) {
      codeRecoveryMutation.mutateAsync(recoverPassword(data.email, emailCode))
        .then(response => response?.data?.recoverPassword?.status === 200 && setIsValidCode(true));
    }
    // When Reset Password
    if (isValidEmail && emailCode && isValidCode) {
      await recoverPasswordMutation.mutateAsync(recoverPassword(data.email, emailCode, data.newPassword));
    }
  };

  return (
    <div className="login-pages">
      <div className="content-login-page">
        <div className="detail-login-page">
          {/* logo login cypherleak */}
          <div className="logo-login">
            <CypherLeakLogo />
          </div>
          {/* sign-in form */}
          <form className="form-login" onSubmit={handleSubmit(onSubmit)}>
            {/* Email Address Input */}
            {!isValidEmail && (
              <InputForm
                label="E-mail address"
                info={errors.email?.message || "We'll send you a code to your email address"}
                isInputError={isDefined(errors.email?.message)}
                rest={{
                  type: "text",
                  ...register("email", {
                    required: "Please fill out this required field",
                    pattern: { value: validEmailPattern, message: "Please fill out valid email" },
                  }),
                }}
              />
            )}
            {/* Verification Code Input | When Valid Email */}
            {isValidEmail && !isValidCode && (
              <VerificationCodeInput
                twoFactorCode={emailCode}
                onChangeCode={setEmailCode}
                label="Please check your email for a message with your code."
              />
            )}
            {/* Reset Password  | When Valid Code Verficiation */}
            {isValidEmail && isValidCode && (
              <>
                <InputForm
                  label="New password"
                  isInputError={isDefined(errors.newPassword?.message)}
                  info={errors.newPassword?.message}
                  rest={{
                    type: "password",
                    ...register("newPassword", {
                      required: "Please fill out the new password",
                      pattern: {
                        value: validPasswordPattern,
                        message: "Weak password. The password does not match with the required criteria",
                      },
                    }),
                  }}
                />
                <InputForm
                  label="Confirm password"
                  isInputError={isDefined(errors.confirmPassword?.message)}
                  info={errors.confirmPassword?.message}
                  rest={{
                    type: "password",
                    ...register("confirmPassword", {
                      required: "Please fill out the confirm password",
                      validate: (value, formValues) => (value !== formValues.newPassword ? "Confirm password does not match" : true),
                    }),
                  }}
                />
              </>
            )}
            {/* Forgot Password */}
            <div className="d-flex justify-content-end form-check">
              <Link className="forgot-password" to={"/login"}>
                Log in with password
              </Link>
            </div>
            {/* sign-in button */}
            <div className="bloc-btn-default d-flex flex-column gap-3">
              <PrimaryButton rest={{ type: "submit" , disabled: isAnyMutationPending() }}>
                {isAnyMutationPending() ? 'Pending...' : (isValidEmail && isValidCode) ? "Reset password" : "Continue"} <i className="icon-arrow-light-blanc" />
              </PrimaryButton>
              {/* Retourn to Forgot Password Form | When Valid Email */}
              {isValidEmail && !isValidCode && (
                <SecondaryButton rest={{ onClick: resetForm }} >
                  Cancel
                </SecondaryButton>
              )}
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}
